import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';

const KYCHistory = () => {
    const [historyCards, setHistoryCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [processingId, setProcessingId] = useState(null); 
    const [filter, setFilter] = useState('ALL'); // 'ALL', 'VERIFIED', 'REJECTED'

    const token = localStorage.getItem("adminToken") || localStorage.getItem("token");

    // 1. 🔥 Fetch History Data & Flatten
    const fetchHistoryRequests = async () => {
        try {
            const res = await fetch(`${API_URL}/api/kyc/admin/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            
            if (data.success) {
                const flattenedCards = [];

                data.data.forEach(user => {
                    // Extract only VERIFIED or REJECTED docs
                    if (['VERIFIED', 'REJECTED'].includes(user.kyc?.pan?.status)) {
                        flattenedCards.push({ userBase: user, docType: 'pan', details: user.kyc.pan, cardId: `${user._id}_pan` });
                    }
                    if (['VERIFIED', 'REJECTED'].includes(user.kyc?.bank?.status)) {
                        flattenedCards.push({ userBase: user, docType: 'bank', details: user.kyc.bank, cardId: `${user._id}_bank` });
                    }
                    if (['VERIFIED', 'REJECTED'].includes(user.kyc?.upi?.status)) {
                        flattenedCards.push({ userBase: user, docType: 'upi', details: user.kyc.upi, cardId: `${user._id}_upi` });
                    }
                });

                // Sort by date (newest first)
                flattenedCards.sort((a, b) => new Date(b.details.submittedAt) - new Date(a.details.submittedAt));
                setHistoryCards(flattenedCards);
            }
        } catch (error) {
            console.error("Failed to fetch history", error);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchHistoryRequests();
        // eslint-disable-next-line
    }, []);

    // 🔥 Manual Refresh Function
    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchHistoryRequests();
    };

    // 2. 🔥 Handle Re-Approve Action (Agar Reject ho gaya tha toh)
    const handleReApprove = async (userId, docType, cardId) => {
        const confirmAction = window.confirm(`Are you sure you want to Re-Approve this ${docType.toUpperCase()}?`);
        if (!confirmAction) return;

        setProcessingId(cardId);

        try {
            const res = await fetch(`${API_URL}/api/kyc/admin/verify/${userId}`, {
                method: "PUT",
                headers: { 
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}` 
                },
                body: JSON.stringify({ status: 'VERIFIED', rejectionReason: "", docType: docType })
            });

            const data = await res.json();

            if (data.success) {
                // Update UI Instantly without full reload
                setHistoryCards(prevCards => 
                    prevCards.map(card => 
                        card.cardId === cardId ? { ...card, details: { ...card.details, status: 'VERIFIED' } } : card
                    )
                );
            } else {
                alert(data.message || "Action failed");
            }
        } catch (error) {
            alert("Network error. Please try again.");
        } finally {
            setProcessingId(null);
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    // Filtering Logic
    const filteredCards = historyCards.filter(card => {
        if (filter === 'ALL') return true;
        return card.details.status === filter;
    });

    return (
        <div className="container-fluid py-4 d-flex flex-column" style={{ backgroundColor: "#f8f9fa", height: "100vh", overflow: "hidden" }}>
            
            {/* Header Area (Fixed at top) */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
                <div>
                    <h3 className="fw-bolder text-dark mb-1" style={{ letterSpacing: "-0.5px" }}>
                        <i className="bi bi-clock-history text-primary me-2"></i> 
                        KYC History
                    </h3>
                    <p className="text-muted small mb-0">View all processed documents and modify statuses.</p>
                </div>
                
                <div className="d-flex align-items-center gap-3">
                    {/* Refresh Button */}
                    <button 
                        className="btn btn-outline-secondary rounded-circle shadow-sm d-flex align-items-center justify-content-center bg-white" 
                        style={{ width: "40px", height: "40px" }}
                        onClick={handleRefresh}
                        disabled={loading || isRefreshing}
                    >
                        <i className="bi bi-arrow-clockwise fs-5" style={isRefreshing ? { animation: 'spinner-border .75s linear infinite' } : {}}></i>
                    </button>

                    {/* Stats */}
                    <div className="bg-white px-3 py-2 rounded-pill shadow-sm border fw-bold text-dark d-none d-md-block">
                        Total Records: <span className="text-primary">{historyCards.length}</span>
                    </div>
                </div>
            </div>

            {/* 🔥 PREMIUM FEATURE: Filters */}
            <div className="mb-4 flex-shrink-0">
                <div className="btn-group shadow-sm" role="group">
                    <button type="button" className={`btn ${filter === 'ALL' ? 'btn-dark' : 'btn-white border'}`} onClick={() => setFilter('ALL')}>
                        All Records
                    </button>
                    <button type="button" className={`btn ${filter === 'VERIFIED' ? 'btn-success' : 'btn-white border'}`} onClick={() => setFilter('VERIFIED')}>
                        <i className="bi bi-check-circle me-1"></i> Verified
                    </button>
                    <button type="button" className={`btn ${filter === 'REJECTED' ? 'btn-danger' : 'btn-white border'}`} onClick={() => setFilter('REJECTED')}>
                        <i className="bi bi-x-circle me-1"></i> Rejected
                    </button>
                </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-grow-1 overflow-auto pe-2" style={{ scrollbarWidth: "thin" }}>
                {(loading || isRefreshing) ? (
                    // SHIMMER EFFECT
                    <div className="row g-4 pb-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="col-md-6">
                                <div className="card shadow-sm border-0 h-100 rounded-4 p-4 placeholder-glow bg-white">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="placeholder rounded-circle me-3" style={{width: '40px', height: '40px'}}></div>
                                        <div className="w-50">
                                            <div className="placeholder rounded mb-1 w-100" style={{height: '15px'}}></div>
                                            <div className="placeholder rounded w-75" style={{height: '10px'}}></div>
                                        </div>
                                    </div>
                                    <div className="placeholder rounded w-100 mb-2" style={{height: '80px'}}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredCards.length === 0 ? (
                    <div className="text-center bg-white p-5 rounded-4 shadow-sm border mt-4" style={{ maxWidth: "500px", margin: "0 auto" }}>
                        <div className="bg-secondary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                            <i className="bi bi-folder2-open text-secondary" style={{ fontSize: "3rem" }}></i>
                        </div>
                        <h4 className="fw-bold text-dark">No Records Found</h4>
                        <p className="text-muted">There are no {filter !== 'ALL' ? filter.toLowerCase() : ''} KYC records in the history yet.</p>
                    </div>
                ) : (
                    <div className="row g-4 pb-4">
                        {filteredCards.map((item) => {
                            const isVerified = item.details.status === 'VERIFIED';

                            return (
                                <div key={item.cardId} className="col-md-6">
                                    <div 
                                        className="card shadow-sm border-0 h-100 rounded-4 overflow-hidden position-relative" 
                                        style={{ transition: "all 0.3s ease", backgroundColor: "#fff", borderLeft: `5px solid ${isVerified ? '#198754' : '#dc3545'}` }}
                                    >
                                        
                                        <div className="p-4 pb-0">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="d-flex align-items-center gap-2">
                                                    <img 
                                                        src={item.userBase.avatar || "https://cdn-icons-png.flaticon.com/512/149/149071.png"} 
                                                        alt="avatar" 
                                                        className="rounded-circle border"
                                                        style={{width: '45px', height: '45px', objectFit: 'cover'}}
                                                    />
                                                    <div>
                                                        <h6 className="fw-bold mb-0 text-dark">{item.userBase.name}</h6>
                                                        <small className="text-muted d-block" style={{fontSize: "11px"}}>{item.userBase.email}</small>
                                                    </div>
                                                </div>
                                                
                                                {/* Status Badge */}
                                                {isVerified ? (
                                                    <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 shadow-sm">
                                                        <i className="bi bi-check-circle-fill me-1"></i> VERIFIED
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 shadow-sm">
                                                        <i className="bi bi-x-circle-fill me-1"></i> REJECTED
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="card-body px-4 pt-2">
                                            <div className="bg-light rounded-3 p-3 border border-secondary-subtle position-relative">
                                                
                                                {/* Document Type Label */}
                                                <span className="position-absolute top-0 end-0 bg-dark text-white rounded-bottom-start-3 px-2 py-1" style={{fontSize: '10px', fontWeight: 'bold'}}>
                                                    {item.docType.toUpperCase()}
                                                </span>

                                                {item.docType === 'pan' && (
                                                    <div className="d-flex align-items-center gap-3 mt-2">
                                                        <a href={item.details.photoUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                                            <img src={item.details.photoUrl} alt="PAN" className="rounded-3 border object-fit-cover shadow-sm" style={{ width: '80px', height: '50px' }} />
                                                        </a>
                                                        <div>
                                                            <div className="small text-muted fw-medium">PAN Number</div>
                                                            <div className="fw-bold text-dark">{item.details.number}</div>
                                                        </div>
                                                    </div>
                                                )}

                                                {item.docType === 'bank' && (
                                                    <div className="mt-2">
                                                        <div className="d-flex justify-content-between">
                                                            <span className="small text-muted fw-medium">A/c No:</span>
                                                            <span className="fw-bold text-dark">{item.details.accountNumber}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mt-1">
                                                            <span className="small text-muted fw-medium">IFSC:</span>
                                                            <span className="fw-bold text-dark">{item.details.ifsc}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {item.docType === 'upi' && (
                                                    <div className="mt-2 d-flex align-items-center gap-2">
                                                        <i className="bi bi-qr-code fs-3 text-secondary"></i>
                                                        <div>
                                                            <span className="small text-muted fw-medium d-block">UPI ID:</span>
                                                            <span className="fw-bold text-primary">{item.details.upiId}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Reason if Rejected */}
                                            {!isVerified && item.details.rejectionReason && (
                                                <div className="mt-2 small text-danger">
                                                    <strong>Reason:</strong> {item.details.rejectionReason}
                                                </div>
                                            )}

                                            <div className="mt-3 d-flex justify-content-between align-items-center">
                                                <span className="small text-muted" style={{fontSize: '10px'}}>
                                                    Processed on: {formatDate(item.details.submittedAt)}
                                                </span>
                                                
                                                {/* 🔥 MAGIC BUTTON: Re-Approve logic */}
                                                {!isVerified && (
                                                    <button 
                                                        className="btn btn-sm btn-outline-success rounded-pill fw-bold"
                                                        onClick={() => handleReApprove(item.userBase._id, item.docType, item.cardId)}
                                                        disabled={processingId === item.cardId}
                                                    >
                                                        {processingId === item.cardId ? <span className="spinner-border spinner-border-sm"></span> : 'Mark as Verified'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default KYCHistory;